<script setup lang="tsx">
import { onMounted } from 'vue';
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';
import { fetchHomeMenuHandler } from '~/handlers/fetch-home-menu.handler';
import type { HomeMenuItem } from '~/handlers/ports/api-fetch-home-menu.port';

const menuItems = useState<HomeMenuItem[]>('home-menu-items', () => []);
const menuFetchPromise = useState<Promise<HomeMenuItem[]> | null>(
  'home-menu-fetch-promise',
  () => null,
);

const { isDesktop } = useLayout();

const isNavigationButtonsVisible = computed<boolean>(() => {
  return isDesktop.value;
});

const hoveredItems = ref<Record<number, boolean>>({});

const route = useRoute();

const ACTIVE_COLOR = '#FF6B00';

const SUPPORT_LINK = 'support';

const AUTH_REQUIRED_LINKS = ['/mini-games/6606', '/vip/levels'];

const CATEGORY_PREFIX = '/games/category/';

const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const { openCashbackMenu } = useCashbackMenu();
const { homeMenuItemClickEvent } = useUserJourney();

function isCashbackRoute(url: string): boolean {
  return /[?#]open=cashback|#cashback/.test(url || '');
}

const isItemActive = (linkUrl: string): boolean => {
  const [path = '', queryString] = linkUrl.split('?');

  // Handle /games/category/{slug} → /games/category/all?initialCategory={slug} redirect
  if (
    path.startsWith(CATEGORY_PREFIX) &&
    !path.endsWith('/all') &&
    route.path === CATEGORY_PREFIX + 'all'
  ) {
    const slug = path.slice(CATEGORY_PREFIX.length);

    return route.query.initialCategory === slug;
  }

  const pathMatch = route.path === path || route.path.startsWith(path + '/');

  if (!pathMatch || !queryString) return pathMatch;

  const linkParams = new URLSearchParams(queryString);

  for (const [key, value] of linkParams) {
    if (route.query[key] !== value) return false;
  }

  return true;
};

const isSupportItem = (linkUrl: string): boolean => {
  return linkUrl === SUPPORT_LINK;
};

const handleItemClick = (item: HomeMenuItem, index: number) => {
  if (import.meta.client) homeMenuItemClickEvent(item.id, index + 1);

  if (isSupportItem(item.link_url)) {
    const { openSupportChat } = useLiveSupport();

    openSupportChat();

    return;
  }

  // Cashback: abre o modal sem sair da rota atual
  if (isCashbackRoute(item.link_url)) {
    openCashbackMenu();

    return;
  }

  if (AUTH_REQUIRED_LINKS.includes(item.link_url) && !isAuthenticated.value) {
    openLogin();

    return;
  }

  navigateTo(item.link_url);
};

onMounted(async () => {
  if (menuItems.value.length) return;

  if (!menuFetchPromise.value) {
    menuFetchPromise.value = fetchHomeMenuHandler();
  }

  menuItems.value = await menuFetchPromise.value;
});
</script>

<template>
  <div
    id="divMenuHighlight"
    class="select-none lg:-mt-6 xl:-mt-8 pl-2.5 lg:pl-0 lg:pb-5"
    draggable="false"
    style="min-height: 90px"
  >
    <WidgetsCarousel
      :key="menuItems.length"
      :display="{ spaceBetweenItems: [12, 5] }"
      :enable-navigation-buttons="isNavigationButtonsVisible"
      item-class="snap-start"
      :items="menuItems"
      slider-fade="both"
    >
      <template #item="{ item, index }">
        <div
          class="flex flex-col items-center cursor-pointer pr-2.5"
          @click="handleItemClick(item, index)"
        >
          <div class="relative">
            <div
              :class="[
                'relative w-[54px] h-[54px] lg:w-[64px] lg:h-[64px] rounded-full border border-solid overflow-hidden transition-opacity duration-150 active:opacity-70',
              ]"
              :style="{
                borderColor: isItemActive(item.link_url)
                  ? ACTIVE_COLOR
                  : item.image_border_color ||
                    (!isDesktop ? '#ffffff' : 'rgba(255,255,255,0.6)'),
                borderWidth: isItemActive(item.link_url)
                  ? '2px'
                  : isDesktop
                    ? '1.5px'
                    : '1px',
                background: item.image_bg_color || '#1111d6',
              }"
            >
              <div
                :id="`horizontal-menu-${index}`"
                class="w-[54px] h-[54px] lg:w-[64px] lg:h-[64px] flex items-center justify-center rounded-full"
                @mouseleave="hoveredItems[index] = false"
                @mouseover="hoveredItems[index] = true"
              >
                <img
                  v-if="item.image_url"
                  :alt="item.title"
                  class="w-full h-full object-cover select-none transition-transform duration-150 pointer-events-none"
                  draggable="false"
                  :src="`${item.image_url}/public`"
                  :style="{
                    transform: `scale(${hoveredItems[index] ? 1.05 : 1})`,
                  }"
                />
              </div>
            </div>

            <div
              v-if="item.label"
              :id="`horizontal-menu-label-${index}`"
              class="absolute w-[55px] lg:w-[64px] h-[12px] -bottom-0.5 left-1/2 text-[8px] text-center px-0 rounded-sm overflow-hidden min-h-[12px]"
              :style="{
                display: 'block',
                background: item.label_bg_color || '#ff3400',
                color: item.label_font_color || '#ffffff',
                transform: 'translateX(-50%) skew(-20deg)',
              }"
            >
              <span
                class="inline-block w-full font-plusJakartaSans font-semibold text-[8px] leading-[12px] tracking-wide text-center"
                style="transform: skew(20deg)"
              >
                <p
                  :id="`horizontal-menu-text-${index}`"
                  class="whitespace-normal"
                >
                  {{ item.label }}
                </p>
              </span>
            </div>
          </div>
          <div
            class="text-[10px] lg:text-[11px] tracking-tighter lg:tracking-tight mt-1.5 font-bold text-center"
            :style="{
              color: isItemActive(item.link_url) ? ACTIVE_COLOR : '#FFFFFF',
            }"
          >
            {{ item.title }}
          </div>
        </div>
      </template>
    </WidgetsCarousel>
  </div>
</template>
